import { apiFetch } from '../../utils/apiFetch';
import type { SavedSearchModel } from '../../models/SavedSearchModel';

export class SavedSearchApiError extends Error {
  constructor(
    message: string,
    public status: number
  ) {
    super(message);
    this.name = 'SavedSearchApiError';
  }
}

export default class BeatAPISavedSearchRepository {
  private _url: string;
  private _apiKey: string;

  constructor(url: string, apiKey: string) {
    this._url = url;
    this._apiKey = apiKey;
  }

  set url(url: string) {
    this._url = url;
  }

  set apiKey(apiKey: string) {
    this._apiKey = apiKey;
  }

  /**
   * Fetch a saved search by slug. Returns null on 404 so callers can render a quiet fallback.
   * Throws on network/server errors so unexpected failures surface.
   */
  async getBySlug(slug: string): Promise<SavedSearchModel | null> {
    return this._getOrNull(`${this._url}/${encodeURIComponent(slug)}`);
  }

  /**
   * Fetch the SYSTEM saved search bound to a tag (by tag slug). Returns null when the
   * tag has no saved search assigned. Used by the /tags/:slug frontend flow so the
   * client mirrors the backend's "exactly one tag → merge saved search" behavior
   * (forceMapClustering, amount, etc.).
   */
  async getByTag(tagSlug: string): Promise<SavedSearchModel | null> {
    return this._getOrNull(`${this._url}/by-tag/${encodeURIComponent(tagSlug)}`);
  }

  private async _getOrNull(url: string): Promise<SavedSearchModel | null> {
    const response = await apiFetch(url, {
      method: 'GET',
      apiKey: this._apiKey,
    });

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      throw new SavedSearchApiError(
        `Failed to fetch saved search: ${response.statusText}`,
        response.status
      );
    }

    const body = await response.json();
    return body?.data ?? null;
  }
}
